import { z } from 'zod';
import { currencyCodeSchema, isoDateSchema, uuidSchema } from './index';

// ==================== Category ====================

export const categorySchema = z.object({
  id: uuidSchema,
  name: z.string().min(1).max(60),
  name_normalized: z.string(),
  icon: z.string(),
  is_predefined: z.boolean(),
  moderation_status: z.enum(['pending', 'approved', 'rejected', 'merged']),
  created_by: uuidSchema.nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type Category = z.infer<typeof categorySchema>;

// ==================== Merchant ====================

export const merchantSchema = z.object({
  id: uuidSchema,
  name: z.string().min(1).max(120),
  name_normalized: z.string(),
  default_category_id: uuidSchema.nullable(),
  moderation_status: z.enum(['pending', 'approved', 'rejected', 'merged']),
  created_by: uuidSchema.nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type Merchant = z.infer<typeof merchantSchema>;

// ==================== Expense ====================

export const splitModeSchema = z.enum(['equal', 'percentage', 'amount', 'shares']);
export type SplitMode = z.infer<typeof splitModeSchema>;

export const expenseSchema = z.object({
  id: uuidSchema,
  group_id: uuidSchema,
  merchant_id: uuidSchema.nullable(),
  merchant_name_raw: z.string().min(1).max(120),
  category_id: uuidSchema,
  description: z.string().nullable(),
  amount_cents: z.union([z.string(), z.number()]).transform((v) => BigInt(v)),
  currency: currencyCodeSchema,
  exchange_rate_to_group_currency: z.union([z.string(), z.number()]),
  amount_in_group_currency_cents: z.union([z.string(), z.number()]).transform((v) => BigInt(v)),
  amount_group_currency_override_cents: z
    .union([z.string(), z.number(), z.bigint()])
    .nullable()
    .transform((v) => (v === null || v === undefined ? null : BigInt(v))),
  amount_group_currency_override_ccy: currencyCodeSchema.nullable(),
  split_mode: splitModeSchema,
  expense_date: isoDateSchema,
  tags: z.array(z.string()).default([]),
  is_suggested: z.boolean().default(false),
  created_by: uuidSchema,
  created_at: z.string(),
  updated_at: z.string(),
  deleted_at: z.string().nullable(),
});
export type Expense = z.infer<typeof expenseSchema>;

export const expensePayerSchema = z.object({
  id: uuidSchema,
  expense_id: uuidSchema,
  participant_id: uuidSchema,
  amount_paid_cents: z.union([z.string(), z.number()]).transform((v) => BigInt(v)),
  // Quién es, no sólo su id. Lo embebe el `select` de listar y de leer un gasto
  // para poder pintar su cara en la lista sin una consulta por fila.
  //
  // Opcional porque no todos los caminos lo traen: al crear o editar un gasto,
  // los pagadores se construyen a mano y ahí no hay participante que embeber.
  // Un `.optional()` mantiene ese camino válido sin inventarse datos.
  participant: z
    .object({
      display_name: z.string(),
      avatar_url: z.string().nullable(),
    })
    .nullable()
    .optional(),
});
export type ExpensePayer = z.infer<typeof expensePayerSchema>;

export const expenseParticipantSchema = z.object({
  id: uuidSchema,
  expense_id: uuidSchema,
  participant_id: uuidSchema,
  share_value: z.union([z.string(), z.number()]).nullable(),
  amount_owed_cents: z.union([z.string(), z.number()]).transform((v) => BigInt(v)),
});
export type ExpenseParticipant = z.infer<typeof expenseParticipantSchema>;

// ==================== Expense Create/Update Input ====================

export const expenseCreateInputSchema = z.object({
  group_id: uuidSchema,
  merchant_name: z.string().min(1).max(120),
  merchant_id: uuidSchema.nullable().optional(),
  category_id: uuidSchema.nullable(),
  category_name: z.string().min(1).max(60).optional(),
  category_icon: z.string().optional(),
  description: z.string().max(1000).nullable().optional(),
  amount_cents: z.union([z.bigint(), z.number(), z.string()]),
  currency: currencyCodeSchema,
  amount_group_currency_override_cents: z
    .union([z.bigint(), z.number(), z.string()])
    .nullable()
    .optional(),
  amount_group_currency_override_ccy: currencyCodeSchema.nullable().optional(),
  split_mode: splitModeSchema,
  expense_date: isoDateSchema,
  // true = fila importada (histórico) → los triggers de notificación la ignoran.
  is_import: z.boolean().optional(),
  tags: z.array(z.string().min(1).max(30)).max(10).default([]),
  payers: z
    .array(
      z.object({
        participant_id: uuidSchema,
        amount_paid_cents: z.union([z.bigint(), z.number(), z.string()]),
      })
    )
    .min(1),
  participants: z
    .array(
      z.object({
        participant_id: uuidSchema,
        share_value: z.number().nullable().optional(),
      })
    )
    .min(1),
});
export type ExpenseCreateInput = z.infer<typeof expenseCreateInputSchema>;
